import React, { Suspense, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';

import { getMe } from './store/slices/authSlice';
import LoadingSpinner from './components/common/LoadingSpinner';
import RoleShellLayout from './components/layout/RoleShellLayout';
import ClientWorkspaceTabGuard, {
  PlatformOwnerWorkspaceTabGuard,
} from './pages/cycle/ClientWorkspaceTabGuard';
import WorkspaceIndexRedirect from './pages/cycle/WorkspaceIndexRedirect';
import {
  ClientLegacyTabRedirect,
  ClientDashboardRedirect,
  ClientLegacyNewCycleRedirect,
  ClientLegacyWorkspaceRedirect,
  isStaffRole,
} from './pages/client/ClientRouteRedirects';
import AppRootRedirect from './components/AppRootRedirect';
import WebsiteExternalRedirect from './components/WebsiteExternalRedirect';
import { clearTokenAndRedirectToLogin, loginPathForStoredRole } from './utils/roleLogin';
import {
  Dashboard,
  ClientDashboard,
  ClientDefects,
  TesterDashboard,
  Projects,
  ProjectDetail,
  ClientProjectDetail,
  ClientTestCycles,
  ClientForms,
  ClientIntegrations,
  ClientTeam,
  TesterTestCycles,
  TestCycles,
  TestCycleEntry,
  LegacyCycleRoute,
  TestExecutionRedirect,
  CycleWorkspaceLayout,
  CycleSetupPanel,
  CycleTeamPanel,
  CycleWorkPanel,
  CycleDefectsPanel,
  CycleResultsPanel,
  CycleRewardsPanel,
  CycleTestCasesPanel,
  CycleTestCaseExecutePage,
  CycleFormsPanel,
  CycleMessagesPanel,
  CycleAnnouncementsPanel,
  CycleBudgetPanel,
  CyclePaymentsPanel,
  CycleActivityPanel,
  CycleCreateWizard,
  CreateIssue,
  TesterProfile,
  Devices,
  DeviceCoverage,
  TesterCoverage,
  Tester360,
  TesterProjects,
  Academy,
  TesterUserGuide,
  RegisteredTesters,
  ImportTestersDevices,
  Earnings,
  Billing,
  FinanceReports,
  FinancePayoutApprovals,
  Clients,
  Client360,
  BugTriage,
  BugDetail,
  MyReportedIssues,
  ClientTestCases,
  Reports,
  Settings,
  KYCReview,
  Login,
  Signup,
  TesterLogin,
  CycleInviteAccept,
  TesterSignUp,
  ClientLogin,
  ClientSignUp,
  EmailVerification,
  ForgotPassword,
  ResetPassword,
  LeadManagement,
  PlatformAdminHub,
  AiCommandCenter,
  PlatformAuditLogs,
  PlatformBtsIntegrations,
  PlatformAppTestifySync,
  PlatformRevenueDashboard,
  JobInviteEmails,
  CampaignAnalytics,
  CycleOnboardingPanel,
  AccountDeletionRequests,
  Messages,
  PublicProjectForm,
  TesterAssignedForms,
  TesterPayoutCompliance,
  ProjectFormResponsesPage,
  ProjectFormSubmissionReportPage,
  TesterOpenCycles,
  StaffProjectDetail,
  StaffProjectCycles,
  StaffProjectForms,
  StaffProjectTabView,
  ClientTermsOfService,
  ClientPrivacyPolicy,
  TesterTermsOfService,
  TesterPrivacyPolicy,
} from './routes/lazyPages';

// Role-based Dashboard Route Component
const DashboardRoute = () => {
    const { user: currentUser } = useSelector((state) => state.auth);

    // Testers should see TesterDashboard
    if (currentUser?.role === 'tester') {
        return <TesterDashboard />;
    }

    if (currentUser?.role === 'client' || currentUser?.role === 'client_team') {
        return <ClientDashboardRedirect />;
    }

    return <Dashboard />;
};

/**
 * Test cycles list: staff use TestCycles.js; client / client_team use ClientTestCycles at /app/test-cycles.
 */
const TestCyclesRoute = () => {
    const { user: currentUser } = useSelector((state) => state.auth);

    if (currentUser?.role === 'client' || currentUser?.role === 'client_team') {
        return <ClientLegacyTabRedirect tab="cycles" />;
    }

    if (currentUser?.role === 'tester') {
        return <TesterTestCycles />;
    }

    return <TestCycles />;
};

function ProjectTabRedirect({ tab }) {
  const { id } = useParams();
  return <Navigate to={`/app/projects/${id}?tab=${tab}`} replace />;
}

function ClientDefectsRoute() {
  const { user } = useSelector((state) => state.auth);
  if (user?.role === 'client' || user?.role === 'client_team') {
    return <ClientLegacyTabRedirect tab="defects" />;
  }
  return <ClientDefects />;
}

function ClientTestCasesRoute() {
  const { user } = useSelector((state) => state.auth);
  if (user?.role === 'client' || user?.role === 'client_team') {
    return <ClientLegacyTabRedirect tab="test_cases" />;
  }
  return <ClientTestCases />;
}

function ClientIntegrationsRoute() {
  const { user } = useSelector((state) => state.auth);
  if (user?.role === 'client' || user?.role === 'client_team') {
    return <ClientLegacyTabRedirect tab="integrations" />;
  }
  return <ClientIntegrations />;
}

function ClientFormsRoute() {
  const { user } = useSelector((state) => state.auth);
  if (user?.role === 'client' || user?.role === 'client_team') {
    return <ClientLegacyTabRedirect tab="forms" />;
  }
  return <ClientForms />;
}

function ClientProjectFormsRoute() {
  return <StaffProjectForms />;
}

function ClientProjectCyclesRoute() {
  const { user } = useSelector((state) => state.auth);
  if (isStaffRole(user?.role)) {
    return <StaffProjectCycles />;
  }
  return <ClientTestCycles />;
}

function ClientProjectDefectsRoute() {
  return <ClientDefects />;
}

function ClientProjectTestCasesRoute() {
  return <ClientTestCases />;
}

function ClientProjectIntegrationsNestedRoute() {
  return <ClientIntegrations />;
}

function ReportsRoute() {
  const { user } = useSelector((state) => state.auth);
  if (user?.role === 'client' || user?.role === 'client_team') {
    return <ClientLegacyTabRedirect tab="test_cases" />;
  }
  return <Reports />;
}

function LegacyWorkspaceRoute() {
  const { user } = useSelector((state) => state.auth);
  if (user?.role === 'client' || user?.role === 'client_team') {
    return <ClientLegacyWorkspaceRedirect />;
  }
  return <CycleWorkspaceLayout />;
}

function ClientGuardedWorkspacePanel({ children }) {
  return <ClientWorkspaceTabGuard>{children}</ClientWorkspaceTabGuard>;
}

function AcceptInvitationRedirect() {
  const [searchParams] = useSearchParams();
  const q = searchParams.toString();
  return <Navigate to={q ? `/reset-password?${q}` : '/reset-password'} replace />;
}

const ProjectDetailRoute = () => {
    const { user: currentUser } = useSelector((state) => state.auth);

    if (currentUser?.role === 'client' || currentUser?.role === 'client_team') {
        return <ClientProjectDetail />;
    }

    if (isStaffRole(currentUser?.role)) {
        return <StaffProjectDetail />;
    }

    return <ProjectDetail />;
};

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, loading, token } = useSelector((state) => state.auth);
    const navigate = useNavigate();

    useEffect(() => {
        // Only redirect if:
        // 1. We're done loading AND
        // 2. User is not authenticated AND
        // 3. There's no token (meaning we never had one, or it was cleared after failed verification)
        if (!loading && !isAuthenticated && !token) {
            const redirectPath = loginPathForStoredRole();
            localStorage.removeItem('lastUserRole');
            navigate(redirectPath, { replace: true });
        }
    }, [isAuthenticated, loading, token, navigate]);

    // Avoid a blank white shell while verifying auth (common on mobile).
    if (loading || (token && !isAuthenticated)) {
        return <LoadingSpinner />;
    }

    if (!isAuthenticated) {
        return <LoadingSpinner />;
    }

    return children;
};

const AppShell = () => (
    <ProtectedRoute>
        <Suspense fallback={<LoadingSpinner />}>
            <RoleShellLayout />
        </Suspense>
    </ProtectedRoute>
);

// Redirect to dashboard if already authenticated (for login pages)
const RedirectIfAuthenticated = ({ children }) => {
    const { isAuthenticated, loading, user, token } = useSelector((state) => state.auth);
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && isAuthenticated) {
            if (user?.role === 'tester' && !user?.profileCompleted) {
                navigate('/app/dashboard', { replace: true });
                return;
            }
            navigate('/dashboard', { replace: true });
        }
    }, [isAuthenticated, loading, user, navigate]);

    if (isAuthenticated) {
        return <LoadingSpinner />;
    }

    // Only spinner while an EXISTING session is being verified. Do not gate on
    // `loading` alone: an interactive login sets loading=true, which would
    // unmount the login form mid-submit and destroy its local state - that is
    // how the 2FA step lost `otpRequired` and silently bounced users back to
    // the sign-in form after "OTP sent to your email".
    if (token && loading) {
        return <LoadingSpinner />;
    }

    return children;
};

// Redirect component for test executions without /app prefix
const TestCycleDetailRedirect = () => {
    const { id } = useParams();
    return <Navigate to={`/app/test-cycles/${id}`} replace />;
};

const DefectAliasRedirect = () => {
    const { id } = useParams();
    const { pathname } = useLocation();
    const base = pathname.startsWith('/app/') ? '/app/bugs' : '/bugs';
    return <Navigate to={`${base}/${id}`} replace />;
};

// Public form share links are registered at /public/...; some users/bookmarks use /app/public/...
const PublicProjectFormPathRedirect = () => {
    const { token } = useParams();
    return <Navigate to={`/public/project-form/${token}`} replace />;
};

// Role-based route wrapper for admin-only routes
const AdminOnlyRoute = ({ children }) => {
    const { user } = useSelector((state) => state.auth);
    const isAdmin = ['admin', 'platform_owner', 'platform_test_manager'].includes(user?.role);

    if (!isAdmin) {
        return <Navigate to="/app/dashboard" replace />;
    }

    return children;
};

// Platform staff areas (admin hub, audit logs, BTS, revenue, deletion requests).
// Test managers are included: the API grants them these endpoints, so gating the
// UI more tightly than the backend just sends them to an empty dashboard.
// Client- and lead-facing screens are gated separately, not here.
const PlatformOwnerAdminRoute = ({ children }) => {
    const { user } = useSelector((state) => state.auth);
    if (!['admin', 'platform_owner', 'platform_test_manager'].includes(user?.role)) {
        return <Navigate to="/app/dashboard" replace />;
    }
    return children;
};

// Platform-owner-only screens (e.g. job invite emails). The API enforces the
// same rule; this just keeps other roles off a page they can't use.
const PlatformOwnerOnlyRoute = ({ children }) => {
    const { user } = useSelector((state) => state.auth);
    if (user?.role !== 'platform_owner') {
        return <Navigate to="/app/dashboard" replace />;
    }
    return children;
};

function App() {
    const dispatch = useDispatch();
    const { token, isAuthenticated, loading, error } = useSelector((state) => state.auth);
    const getMeCalledRef = useRef(false);
    const getMeAttemptsRef = useRef(0);
    const maxRetries = 3;
    const isDev = process.env.NODE_ENV === 'development';

    useEffect(() => {
        const tokenInStorage = localStorage.getItem('token');

        if (tokenInStorage && token && !isAuthenticated && !getMeCalledRef.current && getMeAttemptsRef.current < maxRetries) {
            getMeCalledRef.current = true;
            getMeAttemptsRef.current += 1;

            if (isDev) {
                console.log(`Calling getMe() - Attempt ${getMeAttemptsRef.current} of ${maxRetries}`);
            }

            dispatch(getMe())
                .unwrap()
                .then(() => {
                    getMeCalledRef.current = false;
                    getMeAttemptsRef.current = 0;
                })
                .catch((err) => {
                    const errorMessage = String(err || '');
                    if (isDev) {
                        console.error(`getMe() failed (attempt ${getMeAttemptsRef.current}):`, errorMessage);
                    }

                    const isRateLimited =
                        errorMessage.includes('Too many requests') ||
                        errorMessage.includes('429') ||
                        errorMessage.includes('Rate limit');
                    const isAuthError =
                        errorMessage.includes('401') ||
                        errorMessage.includes('Unauthorized') ||
                        errorMessage.includes('Authentication');

                    if (getMeAttemptsRef.current >= maxRetries || isRateLimited || isAuthError) {
                        getMeCalledRef.current = false;
                        getMeAttemptsRef.current = 0;
                        setTimeout(() => clearTokenAndRedirectToLogin(), 500);
                        return;
                    }

                    if (getMeAttemptsRef.current < maxRetries) {
                        setTimeout(() => {
                            if (localStorage.getItem('token')) {
                                getMeCalledRef.current = false;
                            } else {
                                getMeCalledRef.current = false;
                                getMeAttemptsRef.current = 0;
                            }
                        }, 3000);
                    } else {
                        getMeCalledRef.current = false;
                        getMeAttemptsRef.current = 0;
                        setTimeout(() => clearTokenAndRedirectToLogin(), 500);
                    }
                });
        } else if (!tokenInStorage && token) {
            getMeCalledRef.current = false;
            getMeAttemptsRef.current = 0;
        }

        if (!token || isAuthenticated) {
            getMeCalledRef.current = false;
            getMeAttemptsRef.current = 0;
        }
    }, [dispatch, token, isAuthenticated, loading, error, isDev]);

    // Single safety timeout — covers throttle + retries + slow API (~35s)
    useEffect(() => {
        if (!loading || !token || isAuthenticated) return undefined;

        const timeoutId = setTimeout(() => {
            if (loading && token && !isAuthenticated && localStorage.getItem('token')) {
                clearTokenAndRedirectToLogin();
            }
        }, 35000);

        return () => clearTimeout(timeoutId);
    }, [loading, token, isAuthenticated]);

    return (
        <Suspense fallback={<LoadingSpinner />}>
        <Routes>
            {/* Public login/signup routes */}
            <Route path="/login" element={<RedirectIfAuthenticated><Login /></RedirectIfAuthenticated>} />

            <Route path="/tester-login" element={<RedirectIfAuthenticated><TesterLogin /></RedirectIfAuthenticated>} />
            <Route path="/tester-signup" element={<RedirectIfAuthenticated><TesterSignUp /></RedirectIfAuthenticated>} />
            {/* Authenticated OAuth/email testers with incomplete profiles (full 5-step form) */}
            <Route path="/tester-complete-profile" element={<TesterSignUp />} />
            <Route path="/client-login" element={<RedirectIfAuthenticated><ClientLogin /></RedirectIfAuthenticated>} />
            {/* Public customer signup disabled — platform owner invites clients */}
            <Route path="/client-signup" element={<WebsiteExternalRedirect path="/contact" />} />

            {/* App root — the app serves no marketing pages: visitors go to login, signed-in users to the dashboard */}
            <Route path="/" element={<AppRootRedirect />} />

            {/* Legal documents linked from login/signup (formerly on the marketing site) */}
            <Route path="/legal/client-terms" element={<ClientTermsOfService />} />
            <Route path="/legal/client-privacy" element={<ClientPrivacyPolicy />} />
            <Route path="/legal/tester-terms" element={<TesterTermsOfService />} />
            <Route path="/legal/tester-privacy" element={<TesterPrivacyPolicy />} />

            <Route path="/contact-sales" element={<WebsiteExternalRedirect path="/contact" />} />

            {/* Email verification route - public */}
            <Route path="/verify-email/:token" element={<EmailVerification />} />

            {/* Forgot password route - public */}
            <Route path="/forgot-password" element={<RedirectIfAuthenticated><ForgotPassword /></RedirectIfAuthenticated>} />

            {/* Reset password route - public */}
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invitation" element={<AcceptInvitationRedirect />} />
            <Route path="/cycle-invite/:token" element={<CycleInviteAccept />} />
            <Route path="/app/cycle-invite/:token" element={<CycleInviteAccept />} />

            {/* Public project form (share link) — must stay outside /app; API is /api/public/project-forms */}
            <Route path="/public/project-form/:token" element={<PublicProjectForm />} />
            <Route path="/app/public/project-form/:token" element={<PublicProjectFormPathRedirect />} />

            {/* Protected app routes — RoleShellLayout for client/tester mockup UI */}
            <Route element={<AppShell />}>
            <Route path="/dashboard" element={<DashboardRoute />} />
            <Route path="/app/dashboard" element={<DashboardRoute />} />
            <Route path="/users" element={<AdminOnlyRoute><RegisteredTesters /></AdminOnlyRoute>} />
            <Route path="/app/users" element={<AdminOnlyRoute><RegisteredTesters /></AdminOnlyRoute>} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:id" element={<ProjectDetailRoute />} />
            <Route path="/app/projects" element={<Projects />} />
            <Route path="/app/defects" element={<ClientDefectsRoute />} />
            <Route path="/app/forms" element={<ClientFormsRoute />} />
            <Route path="/app/integrations" element={<ClientIntegrationsRoute />} />
            <Route path="/app/projects/:projectId/test-executions/:executionId" element={<TestExecutionRedirect />} />
            <Route path="/app/projects/:id/forms/:formId/responses" element={<ProjectFormResponsesPage />} />
            <Route path="/app/projects/:id/forms/:formId/submissions/:submissionId" element={<ProjectFormSubmissionReportPage />} />
            <Route path="/app/projects/:id/new-cycle" element={<ClientLegacyNewCycleRedirect />} />
            <Route path="/app/projects/:id/cycles/new" element={<CycleCreateWizard />} />
            <Route path="/app/projects/:id/cycles/:cycleId/workspace" element={<CycleWorkspaceLayout />}>
              <Route index element={<WorkspaceIndexRedirect />} />
              <Route path="setup" element={<CycleSetupPanel />} />
              <Route path="team" element={<ClientGuardedWorkspacePanel><CycleTeamPanel /></ClientGuardedWorkspacePanel>} />
              <Route path="work" element={<ClientGuardedWorkspacePanel><CycleWorkPanel /></ClientGuardedWorkspacePanel>} />
              <Route path="defects" element={<CycleDefectsPanel />} />
              <Route path="results" element={<CycleResultsPanel />} />
              <Route path="rewards" element={<ClientGuardedWorkspacePanel><CycleRewardsPanel /></ClientGuardedWorkspacePanel>} />
              <Route path="test-cases" element={<ClientGuardedWorkspacePanel><CycleTestCasesPanel /></ClientGuardedWorkspacePanel>} />
              <Route path="test-cases/:testCaseId/execute" element={<ClientGuardedWorkspacePanel><CycleTestCaseExecutePage /></ClientGuardedWorkspacePanel>} />
              <Route path="forms" element={<CycleFormsPanel />} />
              <Route path="announcements" element={<CycleAnnouncementsPanel />} />
              <Route path="budget" element={<ClientGuardedWorkspacePanel><CycleBudgetPanel /></ClientGuardedWorkspacePanel>} />
              <Route path="payments" element={<PlatformOwnerWorkspaceTabGuard><CyclePaymentsPanel /></PlatformOwnerWorkspaceTabGuard>} />
              <Route path="activity" element={<CycleActivityPanel />} />
              <Route path="onboarding" element={<ClientGuardedWorkspacePanel><CycleOnboardingPanel /></ClientGuardedWorkspacePanel>} />
              <Route path="messages" element={<ClientGuardedWorkspacePanel><CycleMessagesPanel /></ClientGuardedWorkspacePanel>} />
            </Route>
            <Route path="/app/projects/:id/cycles/:cycleId/create-issue" element={<CreateIssue />} />
            <Route path="/app/projects/:id/cycles" element={<ClientProjectCyclesRoute />} />
            <Route path="/app/projects/:id/defects" element={<ClientProjectDefectsRoute />} />
            <Route path="/app/projects/:id/test-cases" element={<ClientProjectTestCasesRoute />} />
            <Route path="/app/projects/:id/integrations" element={<ClientProjectIntegrationsNestedRoute />} />
            <Route path="/app/projects/:id/forms" element={<ClientProjectFormsRoute />} />
            <Route path="/app/projects/:id/triage" element={<StaffProjectTabView tabHash="triage" />} />
            <Route path="/app/projects/:id/team" element={<StaffProjectTabView tabHash="team" />} />
            <Route path="/app/projects/:id/reports" element={<StaffProjectTabView tabHash="reports" />} />
            <Route path="/app/projects/:id/wiki" element={<StaffProjectTabView tabHash="wiki" />} />
            <Route path="/app/projects/:id" element={<ProjectDetailRoute />} />
            <Route path="/test-cycles" element={<Navigate to="/app/test-cycles" replace />} />
            <Route path="/test-cycles/:id" element={<TestCycleDetailRedirect />} />
            <Route path="/app/open-cycles" element={<TesterOpenCycles />} />
            <Route path="/app/test-cycles" element={<TestCyclesRoute />} />
            <Route path="/app/test-cycles/:cycleId/workspace" element={<LegacyWorkspaceRoute />}>
                <Route index element={<WorkspaceIndexRedirect />} />
                <Route path="setup" element={<CycleSetupPanel />} />
                <Route path="team" element={<ClientGuardedWorkspacePanel><CycleTeamPanel /></ClientGuardedWorkspacePanel>} />
                <Route path="work" element={<ClientGuardedWorkspacePanel><CycleWorkPanel /></ClientGuardedWorkspacePanel>} />
                <Route path="defects" element={<CycleDefectsPanel />} />
                <Route path="results" element={<CycleResultsPanel />} />
                <Route path="rewards" element={<ClientGuardedWorkspacePanel><CycleRewardsPanel /></ClientGuardedWorkspacePanel>} />
                <Route path="test-cases" element={<ClientGuardedWorkspacePanel><CycleTestCasesPanel /></ClientGuardedWorkspacePanel>} />
              <Route path="test-cases/:testCaseId/execute" element={<ClientGuardedWorkspacePanel><CycleTestCaseExecutePage /></ClientGuardedWorkspacePanel>} />
                <Route path="forms" element={<CycleFormsPanel />} />
                <Route path="announcements" element={<CycleAnnouncementsPanel />} />
                <Route path="budget" element={<ClientGuardedWorkspacePanel><CycleBudgetPanel /></ClientGuardedWorkspacePanel>} />
                <Route path="payments" element={<PlatformOwnerWorkspaceTabGuard><CyclePaymentsPanel /></PlatformOwnerWorkspaceTabGuard>} />
                <Route path="activity" element={<CycleActivityPanel />} />
                <Route path="onboarding" element={<ClientGuardedWorkspacePanel><CycleOnboardingPanel /></ClientGuardedWorkspacePanel>} />
                <Route path="messages" element={<ClientGuardedWorkspacePanel><CycleMessagesPanel /></ClientGuardedWorkspacePanel>} />
            </Route>
            <Route path="/app/test-cycles/:id/legacy" element={<LegacyCycleRoute />} />
            <Route path="/app/test-cycles/:id" element={<TestCycleEntry />} />
            <Route path="/app/test-cycles/:id/create-issue" element={<CreateIssue />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/app/billing" element={<Billing />} />
            <Route path="/app/finance-reports" element={<PlatformOwnerAdminRoute><FinanceReports /></PlatformOwnerAdminRoute>} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/app/clients" element={<Clients />} />
            <Route path="/app/clients/:clientId" element={<AdminOnlyRoute><Client360 /></AdminOnlyRoute>} />
            <Route path="/bug-triage" element={<AdminOnlyRoute><BugTriage /></AdminOnlyRoute>} />
            <Route path="/app/bug-triage" element={<AdminOnlyRoute><BugTriage /></AdminOnlyRoute>} />
            <Route path="/bugs/:id" element={<BugDetail />} />
            <Route path="/app/bugs/:id" element={<BugDetail />} />
            <Route path="/defects/:id" element={<DefectAliasRedirect />} />
            <Route path="/app/defects/:id" element={<DefectAliasRedirect />} />
            <Route path="/app/test-cases" element={<ClientTestCasesRoute />} />
            <Route path="/reports" element={<Navigate to="/app/reports" replace />} />
            <Route path="/app/reports" element={<ReportsRoute />} />
            <Route
              path="/app/reports/acquisition"
              element={
                <PlatformOwnerAdminRoute>
                  <CampaignAnalytics />
                </PlatformOwnerAdminRoute>
              }
            />
            <Route path="/settings" element={<Settings />} />
            <Route path="/app/settings" element={<Settings />} />
            <Route path="/app/team" element={<ClientTeam />} />
            <Route path="/app/kyc-review" element={<KYCReview />} />
            <Route path="/app/payout-approvals" element={<PlatformOwnerAdminRoute><FinancePayoutApprovals /></PlatformOwnerAdminRoute>} />
            <Route path="/app/admin/import-testers-devices" element={<AdminOnlyRoute><ImportTestersDevices /></AdminOnlyRoute>} />
            <Route path="/tester-profile" element={<TesterProfile />} />
            <Route path="/app/tester-profile" element={<TesterProfile />} />
            <Route path="/devices" element={<Devices />} />
            <Route path="/app/devices" element={<Devices />} />
            <Route path="/academy" element={<Academy />} />
            <Route path="/app/academy" element={<Academy />} />
            <Route path="/user-guide" element={<TesterUserGuide />} />
            <Route path="/app/user-guide" element={<TesterUserGuide />} />
            <Route path="/coverage" element={<AdminOnlyRoute><DeviceCoverage /></AdminOnlyRoute>} />
            <Route path="/app/coverage" element={<AdminOnlyRoute><DeviceCoverage /></AdminOnlyRoute>} />
            <Route path="/app/tester-coverage" element={<TesterCoverage />} />
            <Route path="/app/testers/:userId/projects" element={<AdminOnlyRoute><TesterProjects /></AdminOnlyRoute>} />
            <Route path="/app/testers/:userId" element={<AdminOnlyRoute><Tester360 /></AdminOnlyRoute>} />
            <Route path="/earnings" element={<Earnings />} />
            <Route path="/app/earnings" element={<Earnings />} />
            <Route path="/my-reported-issues" element={<MyReportedIssues />} />
            <Route path="/app/my-reported-issues" element={<MyReportedIssues />} />
            <Route path="/app/tester-forms" element={<TesterAssignedForms />} />
            <Route path="/app/payout-compliance" element={<TesterPayoutCompliance />} />
            <Route path="/app/leads" element={<AdminOnlyRoute><LeadManagement /></AdminOnlyRoute>} />
            <Route path="/app/command-center" element={<PlatformOwnerAdminRoute><AiCommandCenter /></PlatformOwnerAdminRoute>} />
            <Route path="/app/admin" element={<PlatformOwnerAdminRoute><PlatformAdminHub /></PlatformOwnerAdminRoute>} />
            <Route path="/app/admin/audit-logs" element={<PlatformOwnerAdminRoute><PlatformAuditLogs /></PlatformOwnerAdminRoute>} />
            <Route path="/app/admin/bts-integrations" element={<PlatformOwnerAdminRoute><PlatformBtsIntegrations /></PlatformOwnerAdminRoute>} />
            <Route path="/app/admin/apptestify-sync" element={<PlatformOwnerAdminRoute><PlatformAppTestifySync /></PlatformOwnerAdminRoute>} />
            <Route path="/app/admin/revenue" element={<PlatformOwnerAdminRoute><PlatformRevenueDashboard /></PlatformOwnerAdminRoute>} />
            <Route path="/app/admin/job-invites" element={<PlatformOwnerOnlyRoute><JobInviteEmails /></PlatformOwnerOnlyRoute>} />
            <Route path="/app/admin/campaign-analytics" element={<Navigate to="/app/reports/acquisition" replace />} />
            <Route path="/app/account-deletion-requests" element={<PlatformOwnerAdminRoute><AccountDeletionRequests /></PlatformOwnerAdminRoute>} />
            <Route path="/app/messages" element={<Messages />} />
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
        </Routes>
        </Suspense>
    );
}

export default App;
